export type MoveUpOption = {
  id: string;
  name: string;
  short: string;
  equityAccess: string;
  offerStrength: string;
  doublePaymentRisk: string;
  speed: string;
  mainRisk: string;
  how: string;
  bestFor: string;
  disclosure: string;
};

export const MOVE_UP_OPTIONS: MoveUpOption[] = [
  {
    id: "bridge-loan",
    name: "Bridge loan",
    short: "Short-term loan against your current home's equity",
    equityAccess: "High — typically sized against your current equity, net of the existing mortgage",
    offerStrength: "Strong. Lets you write a non-contingent offer",
    doublePaymentRisk: "High while both homes are held",
    speed: "Weeks, if the lender offers the product",
    mainRisk: "The old home sells slower than the loan term assumes",
    how: "A short-term loan secured by your departing home that funds the down payment on the new one and is repaid at the sale.",
    bestFor: "Competitive markets where a sale contingency effectively disqualifies your offer and your equity is real but locked up.",
    disclosure:
      "Bridge loan availability, rates, origination fees, term length, and qualification standards vary widely by lender and market. Some lenders no longer offer them at all. Get the payoff terms in writing before you rely on one.",
  },
  {
    id: "heloc",
    name: "HELOC on the current home",
    short: "Revolving line drawn against existing equity",
    equityAccess: "Moderate to high, depending on the approved credit limit",
    offerStrength: "Strong. Cash in hand behaves like a non-contingent offer",
    doublePaymentRisk: "High — mortgage, line payment, and the new loan",
    speed: "Must be opened before you list; underwriting takes weeks",
    mainRisk: "Most lenders will not open or fund a line on a listed home",
    how: "You open a line of credit against your current home before listing it, draw on it for the down payment, and pay it off from the sale proceeds.",
    bestFor: "Homeowners planning far enough ahead to open the line while the home is still off the market, who want cheaper access to equity than a bridge loan.",
    disclosure:
      "HELOC rates are commonly variable, draw and repayment periods differ by lender, and many lenders freeze or decline lines on homes that are listed or under contract. Terms, fees, and eligibility vary by lender.",
  },
  {
    id: "401k-loan",
    name: "401(k) or retirement loan",
    short: "Borrowing from your own retirement plan",
    equityAccess: "Capped by plan rules, independent of home equity",
    offerStrength: "Strong. Funds arrive as cash",
    doublePaymentRisk: "High — payroll repayment plus two housing payments",
    speed: "Often the fastest option, days to a couple of weeks",
    mainRisk: "Repayment acceleration if you leave the job",
    how: "Your employer plan lends you a portion of your vested balance, repaid through payroll deduction with interest paid back to your own account.",
    bestFor: "Buyers who need a modest gap amount quickly and have stable employment through the repayment period.",
    disclosure:
      "Plan loans are governed by your specific plan document, not by a universal rule: limits, repayment terms, whether loans are permitted at all, and the consequences of separation from employment vary. Unpaid balances may be treated as a distribution with tax and penalty consequences. Confirm details with your plan administrator and a tax professional.",
  },
  {
    id: "sale-contingency",
    name: "Home sale contingency",
    short: "Purchase conditioned on your current home selling",
    equityAccess: "None up front — you use the actual sale proceeds",
    offerStrength: "Weak in competitive markets; workable in slow ones",
    doublePaymentRisk: "Low — you never carry both loans",
    speed: "No extra financing setup needed",
    mainRisk: "Rejected outright, or accepted with a kick-out clause",
    how: "A contract clause making your purchase conditional on your current home selling (or closing) by a stated date, with a right to cancel if it does not.",
    bestFor: "Balanced or slow markets, or when no financing tool is available and you cannot carry two payments.",
    disclosure:
      "Whether sellers accept contingent offers, and what a kick-out clause allows, is set by local contract forms, negotiation, and market conditions. Your contract language controls.",
  },
  {
    id: "rent-back",
    name: "Rent-back (post-occupancy)",
    short: "Sell first, then rent your old home back briefly",
    equityAccess: "Full — the sale closes before you buy",
    offerStrength: "Strongest. You shop with known cash",
    doublePaymentRisk: "None — you own one home at a time",
    speed: "Depends on how fast your home sells",
    mainRisk: "Limited length; you may still need interim housing",
    how: "Your sale closes and you stay in the home as a tenant of the new owner for a set period at an agreed daily rate, then move once into the new home.",
    bestFor: "Sellers in a market where their home moves quickly, buying into a market where they can close within a month or two.",
    disclosure:
      "Many lenders limit owner-occupied buyers to a rent-back of about 60 days, and some jurisdictions treat longer stays as a tenancy with tenant protections. Occupancy fees, insurance duties, and any escrow holdback are negotiated in your contract.",
  },
  {
    id: "extended-closing",
    name: "Extended closing",
    short: "Negotiate a longer escrow on the purchase",
    equityAccess: "Full, if the sale closes inside the extension",
    offerStrength: "Moderate. Sellers may prefer a faster close",
    doublePaymentRisk: "Low if timed correctly",
    speed: "Adds 30 to 60+ days to the purchase timeline",
    mainRisk: "Rate lock expiry and a seller who will not wait",
    how: "You negotiate a purchase closing date far enough out that your own sale can close first, funding the purchase without a bridge.",
    bestFor: "Motivated or flexible sellers, new construction, and markets where a longer escrow is normal.",
    disclosure:
      "Rate lock lengths, extension fees, and seller willingness vary. A longer lock typically costs more, and lock extensions are priced by the lender at the time of request.",
  },
  {
    id: "keep-as-rental",
    name: "Keep the current home as a rental",
    short: "Do not sell — convert it to an income property",
    equityAccess: "None, unless paired with a HELOC or cash-out refinance",
    offerStrength: "Depends entirely on qualifying with both mortgages",
    doublePaymentRisk: "Ongoing, offset by rent if the unit stays leased",
    speed: "No sale timeline at all",
    mainRisk: "Qualifying with both payments, vacancy, and landlord duties",
    how: "You keep the departing home, lease it out, and buy the new home as your primary residence — subject to your lender counting (or not counting) the rental income.",
    bestFor: "Owners with a low fixed rate on the old home, solid reserves, and appetite for being a landlord.",
    disclosure:
      "Whether a lender counts departing-residence rental income, and what documentation is required (lease, reserves, appraisal rent schedule), varies by loan program and lender. Converting a primary residence to a rental also has insurance and tax implications — confirm with your insurer and a tax professional.",
  },
  {
    id: "buy-before-you-sell",
    name: "Buy-before-you-sell program",
    short: "A company advances equity or guarantees your sale",
    equityAccess: "High — the program fronts the equity or buys the home",
    offerStrength: "Strong. Typically produces a cash or non-contingent offer",
    doublePaymentRisk: "Varies by program structure",
    speed: "Days to weeks once approved",
    mainRisk: "Program fees and a below-market guaranteed price",
    how: "A provider buys or guarantees your current home, or advances the equity, so you can make a non-contingent offer and move once.",
    bestFor: "Buyers in fast markets who value certainty and one move over squeezing the last dollar out of the sale.",
    disclosure:
      "Program fees, guaranteed-price discounts, eligibility limits, and geographic availability differ widely between providers. Compare total program cost against the price you would likely get on the open market.",
  },
];

export const COMPARISON_FACTORS = [
  { key: "equityAccess", label: "Equity access" },
  { key: "offerStrength", label: "Offer strength" },
  { key: "doublePaymentRisk", label: "Double-payment risk" },
  { key: "speed", label: "Speed to funds" },
  { key: "mainRisk", label: "Main risk" },
] as const;

export type TimelineStage = {
  id: string;
  heading: string;
  when: string;
  items: string[];
};

export const MOVE_UP_TIMELINE: TimelineStage[] = [
  {
    id: "day-90",
    heading: "Day 90 to 61 — Set up your equity access",
    when: "Roughly three months out",
    items: [
      "Get a pre-approval that assumes you still own the current home, and ask what happens to it once the old mortgage is gone.",
      "Ask the lender directly which bridge, HELOC, or departing-residence rental-income options they support — the answer differs by lender.",
      "Apply for a HELOC now if you want one; most lenders will not open a line once the home is listed.",
      "Get a listing consultation and a written net-proceeds estimate so you know the real number, not the Zestimate.",
      "Check your 401(k) plan document for whether loans are permitted, the cap, and the repayment terms on separation.",
      "Price out two to three buy-before-you-sell programs if your market is competitive.",
      "Start decluttering and pre-listing repairs — a faster sale reduces every risk on this page.",
    ],
  },
  {
    id: "day-60",
    heading: "Day 60 to 31 — Choose the structure and shop",
    when: "Roughly two months out",
    items: [
      "Lock in one financing structure and get the terms in writing, including payoff conditions and any fees.",
      "Decide whether you will write contingent or non-contingent offers, and tell your agent so they can position you.",
      "Begin touring in the new market and confirm how competitive offers actually look there this month.",
      "Run your worst-case carrying-cost math: both mortgages, both tax and insurance escrows, and both utility bills for three months.",
      "Confirm whether a rent-back or extended closing is realistic on both sides of the trade.",
      "Line up staging and photography for the current home so you can list on short notice.",
    ],
  },
  {
    id: "day-30",
    heading: "Day 30 to closing — Execute both transactions",
    when: "Final month",
    items: [
      "Write the offer, and match your rate lock length to the realistic closing date rather than the optimistic one.",
      "List the current home the moment the purchase is under contract, or earlier if your structure requires the sale.",
      "Give both closing agents each other's contact details and confirm each one's daily wire cutoff.",
      "Order the payoff statement on the current mortgage and note its expiration date.",
      "Schedule utility transfers with a one-day overlap on each end, and photograph every meter.",
      "Confirm the rent-back or interim housing plan in writing if the dates do not line up.",
      "Verify wiring instructions by phone using a number you looked up yourself, never one from an email.",
    ],
  },
];

export const BUY_BEFORE_SELLING_FAQS = [
  {
    id: "heloc-timing",
    q: "Can I open a HELOC after I list my house?",
    a: "Usually no. Most lenders will not open or fund a home equity line on a property that is already listed for sale, and many will freeze an existing line once they learn the home is on the market. If a HELOC is part of your plan, apply and close the line before the listing goes live — underwriting commonly takes several weeks. Policies vary by lender, so confirm the specific rule in writing before you count on the funds.",
  },
  {
    id: "bridge-vs-heloc",
    q: "Bridge loan or HELOC — which is better for buying before selling?",
    a: "A HELOC is generally cheaper if you can open it before listing: lower fees and interest only on what you draw, though the rate is usually variable. A bridge loan costs more in rate and origination but is purpose-built for this situation, can often be arranged after you are already committed, and is sized against your equity. The practical deciding factor is timing: if the home is already listed, the HELOC door is usually closed and the bridge loan is what is left. Rates, fees, and availability vary by lender.",
  },
  {
    id: "sale-contingency-worth-it",
    q: "Is a home sale contingency worth it?",
    a: "It depends entirely on the market. In a balanced or slow market a contingency costs you nothing up front and protects you from carrying two mortgages. In a competitive market it is often the single reason your offer loses to an otherwise identical clean offer, and sellers who do accept one frequently attach a kick-out clause letting them keep marketing the home and bump you if a better offer arrives. Ask your agent how many contingent offers have actually been accepted in your target area recently.",
  },
  {
    id: "rent-back-basics",
    q: "How does a rent-back work when I sell first?",
    a: "You close the sale, then stay in the home as a tenant of the new owner for an agreed period — commonly up to about 60 days because many lenders require owner-occupied buyers to take possession within that window. The agreement sets a daily or monthly occupancy fee (often roughly the buyer's daily carrying cost), who insures what, the condition the home must be returned in, and sometimes an escrow holdback as security. It is a contract, not a courtesy, and longer stays can trigger tenancy protections in some jurisdictions.",
  },
  {
    id: "program-fees",
    q: "What do buy-before-you-sell programs cost?",
    a: "Structures differ too much for a single number. Some charge a program fee expressed as a percentage of the purchase or sale price, some take a guaranteed-price discount below likely market value, and some do both plus daily carrying charges if the home takes longer to sell. Ask each provider for the total all-in cost in dollars under a realistic timeline and a slow one, then compare that against what a normal open-market sale would net you. Fees and availability vary by provider and market.",
  },
  {
    id: "double-payment",
    q: "What happens if I end up making two mortgage payments?",
    a: "Budget for it before it happens rather than after. Add both principal and interest payments, both tax and insurance escrows, both utility bills, and any bridge or HELOC payment, then multiply by three months as a planning floor. If that number is not something you can cover from income and reserves without touching the money you need at closing, you likely want a sale contingency or a sell-first structure instead of a bridge.",
  },
  {
    id: "qualifying-two-mortgages",
    q: "Can I even qualify for a new mortgage while I still own my home?",
    a: "Often yes, but it depends on your debt-to-income ratio with both housing payments counted. If you plan to rent the departing home, some loan programs let the lender count a portion of the documented rental income to offset the old payment, typically requiring a signed lease and sometimes reserves. Others will not count it at all until there is a payment history. Ask your loan officer to run the numbers both ways — with and without rental income — before you write an offer.",
  },
  {
    id: "old-home-doesnt-sell",
    q: "What if my old home doesn't sell in time?",
    a: "That is the scenario every structure on this page is really being chosen against. With a bridge loan the clock keeps running and you owe interest plus the balance at term. With a HELOC you keep making payments on the drawn balance. With a sale contingency you can typically cancel the purchase under the contract terms and get your earnest money back. Before you commit, ask your lender what happens at the end of the term and ask your agent what a price reduction to move the home in 30 days would look like.",
  },
];

export const EXPERT_REVIEW = {
  name: "Home Approach Editorial Team",
  role: "Homebuying content review",
  reviewDate: "August 2026",
  note:
    "Financing availability, program fees, contingency acceptance, and rent-back rules vary by lender, contract, and local market. This guide explains how the options work; it is not advice about which one to choose.",
};
